/**
 * verificationReviewsApi — Risk Management clears or declines LexisNexis (FraudPoint) holds (Bill 2026-11-02).
 *
 *   GET  /v3/risk/verification/reviews?status=open|decided
 *   POST /v3/risk/verification/reviews/decision  { cartId, extractId?, partyId?, decision: 'cleared' | 'declined', reason }
 *
 * RiskManager role (SuperAdmin passes). Bearer-only; no function key in the bundle.
 */
import { buildHeaders, getBaseUrl, readJsonOrThrow } from './riskSettingsApi';

interface Envelope { success?: boolean; error?: string }

export type VerificationDecisionKind = 'cleared' | 'declined';

export interface VerificationReviewDecision {
  readonly decisionId: string;
  readonly decision: VerificationDecisionKind;
  readonly reason: string;
  readonly decidedBy: string;
  readonly decidedAt: string;
}

export interface VerificationReviewItem {
  readonly cartId: string;
  readonly extractId: string | null;
  readonly partyId: string | null;
  readonly personName: string;
  readonly score: number;
  readonly band: string;
  readonly market: string;
  readonly requirementText: string;
  readonly runAt: string | null;
  readonly runBy: string | null;
  readonly riskIndicators: ReadonlyArray<string>;
  readonly nameAddrSsn: string | null;
  readonly dobMatchLevel: string | null;
  readonly redFlagCount: number;
  readonly branch: string | null;
  readonly officerCode: string | null;
  readonly cartStatus: string;
  readonly testApplication: boolean;
  readonly decision: VerificationReviewDecision | null;
  /** Fraud indices above the review line (Ginger 2026-11-02), e.g. "Friendly Fraud 7"; null when none. */
  readonly indexFlag: string | null;
  /** False when the result is queued for review but not holding the application (fraud-index flag mode). */
  readonly held: boolean;
}

export async function loadVerificationReviews(status: 'open' | 'decided'): Promise<ReadonlyArray<VerificationReviewItem>> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/verification/reviews?status=${status}`, { method: 'GET', headers: await buildHeaders() });
  const body = await readJsonOrThrow<Envelope & { items?: VerificationReviewItem[] }>(resp);
  return body.items ?? [];
}

export async function decideVerification(
  item: Pick<VerificationReviewItem, 'cartId' | 'extractId' | 'partyId'>,
  decision: VerificationDecisionKind,
  reason: string,
): Promise<VerificationReviewDecision> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/verification/reviews/decision`, {
    method: 'POST',
    headers: await buildHeaders(),
    body: JSON.stringify({ cartId: item.cartId, extractId: item.extractId, partyId: item.partyId, decision, reason }),
  });
  const body = await readJsonOrThrow<Envelope & { decision: VerificationReviewDecision }>(resp);
  return body.decision;
}
